/**
 * ============================================================================
 * 문의하기 서비스
 * ============================================================================
 * 
 * Ticket API 호출 서비스
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. getTickets        - 티켓 목록 조회
 * 2. getTicket         - 티켓 상세 조회
 * 3. createTicket      - 티켓 생성
 * 4. uploadFile        - 파일 업로드 (티켓 생성 전 필요)
 * 
 * ============================================================================
 */

import apiClient from '../../core/http/axios';
import {
  Ticket,
  TicketListItem,
  TicketCreateRequest,
  TicketUpdateRequest,
  TicketListResponse,
  TicketSearchParams,
  TicketMessagesResponse,
  TicketMessagesParams,
} from '../types/inquiry';

/**
 * 티켓 목록 조회
 */
export const getTickets = async (params: TicketSearchParams = {}): Promise<TicketListResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.status !== undefined && params.status !== null) {
    queryParams.append('status', params.status);
  }
  if (params.category !== undefined && params.category !== null) {
    queryParams.append('category', params.category);
  }
  if (params.q !== undefined && params.q !== null && params.q.trim() !== '') {
    queryParams.append('q', params.q.trim());
  }
  if (params.offset !== undefined) {
    queryParams.append('offset', params.offset.toString());
  }
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params.include_deleted !== undefined) {
    queryParams.append('include_deleted', params.include_deleted.toString());
  }

  const response = await apiClient.get(`/cs/tickets?${queryParams.toString()}`);
  
  if (response.data.success) {
    return {
      items: response.data.data.items || [],
      total: response.data.data.total || 0,
      limit: response.data.data.limit || 50,
      offset: response.data.data.offset || 0,
    };
  }
  
  throw new Error(response.data.error || '문의 목록을 불러오는데 실패했습니다.');
};

/**
 * 티켓 상세 조회
 */
export const getTicket = async (ticketNo: number): Promise<Ticket> => {
  const response = await apiClient.get(`/cs/tickets/${ticketNo}`);
  
  if (response.data.success) {
    return response.data.data;
  }
  
  throw new Error(response.data.error || '문의를 불러오는데 실패했습니다.');
};

/**
 * 티켓 메시지 목록 조회 (답변 목록)
 */
export const getTicketMessages = async (
  ticketNo: number,
  params: TicketMessagesParams = {}
): Promise<TicketMessagesResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params.include_internal !== undefined) {
    queryParams.append('include_internal', params.include_internal.toString());
  }
  if (params.cursor_message_no !== undefined) {
    queryParams.append('cursor_message_no', params.cursor_message_no.toString());
  }

  const response = await apiClient.get(`/cs/tickets/${ticketNo}/messages?${queryParams.toString()}`);
  
  if (response.data.success) {
    return {
      items: response.data.data.items || [],
      total: response.data.data.total || 0,
      limit: response.data.data.limit || 50,
      next_cursor_message_no: response.data.data.next_cursor_message_no || 0,
    };
  }
  
  throw new Error(response.data.error || '답변 목록을 불러오는데 실패했습니다.');
};

/**
 * 파일 업로드 (티켓 생성 전에 파일을 먼저 업로드하여 UUID를 받아야 함)
 */
export const uploadFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('upload', file);
  formData.append('purpose', 'GENERAL');

  const response = await apiClient.post('/viewer/files/upload', formData, {
    headers: {
      'accept': 'application/json',
      'Content-Type': 'multipart/form-data',
    },
  });
  
  if (response.data.success) {
    return response.data.data.file_uuid;
  }
  
  throw new Error(response.data.error || '파일 업로드에 실패했습니다.');
};

/**
 * 티켓 생성
 */
export const createTicket = async (data: TicketCreateRequest): Promise<Ticket> => {
  const response = await apiClient.post('/cs/tickets', {
    title: data.title,
    content: data.content,
    category: data.category,
    priority: data.priority || 'NORMAL',
    file_uuids: data.file_uuids || [],
  });
  
  if (response.data.success) {
    return response.data.data;
  }
  
  throw new Error(response.data.error || '문의를 생성하는데 실패했습니다.');
};

/**
 * 티켓 수정
 */
export const updateTicket = async (ticketNo: number, data: TicketUpdateRequest): Promise<Ticket> => {
  const response = await apiClient.put(`/cs/tickets/${ticketNo}`, {
    title: data.title,
    content: data.content,
    category: data.category,
    priority: data.priority,
    file_uuids: data.file_uuids || [],
  });
  
  if (response.data.success) {
    return response.data.data;
  }
  
  throw new Error(response.data.error || '문의를 수정하는데 실패했습니다.');
};

/**
 * 티켓 삭제
 */
export const deleteTicket = async (ticketNo: number): Promise<void> => {
  const response = await apiClient.delete(`/cs/tickets/${ticketNo}`);
  
  if (!response.data.success) {
    throw new Error(response.data.error || '문의를 삭제하는데 실패했습니다.');
  }
};
